import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Box,
  Check,
  Printer,
} from "lucide-react";
import { useId } from "react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { EvolutionWorkspace } from "./-evolution-workspace";

import "./-evolution.css";

export const Route = createFileRoute("/prototypes/evolution")({
  component: BearEvolution,
});

const stages = [
  {
    id: "connect",
    number: "01",
    horizon: "The starting point · MVP",
    title: "Work in your tools.",
    description:
      "Assign a change to Bear. It works in your tools, using existing models or building a focused model when the task needs one.",
    outcome: "The same deliverables. Less rework.",
    model: "Model what the task needs",
    diagram: ConnectDiagram,
    tab: "01 · Connected tools",
    exampleTitle: "Bear carries the change through your existing tools.",
    exampleBody:
      "A supplier emails that the specified air-handling unit is unavailable. Your engineer assigns Bear the substitution review, with the email thread and meeting notes as context.",
    steps: [
      [
        "Cross-check",
        "Compare the supplier email, meeting notes, equipment schedule and drawings. Flag conflicting specifications before carrying the change through.",
      ],
      [
        "Do the work",
        "Use the existing Revit model, or model just the unit, connections and clearances needed for the check. Run calculations and prepare revisions through the team's tools.",
      ],
      [
        "Review & issue",
        "Bring the engineer the calculations and proposed changes. After approval, produce the revised document set.",
      ],
    ],
    asideLabel: "How we start",
    asideTitle: "One team. One repeatable workflow.",
    asideBody:
      "Sit with the people producing documents at an architecture firm, engineering firm or GC design team. Connect email, Notion or Granola; code how Bear uses their tools. Agree on the choices it can make and the points that need human review.",
    deliverable:
      "Reviewed calculations + revised sheets + updated equipment schedule",
  },
  {
    id: "build",
    number: "02",
    horizon: "The expansion",
    title: "Bring the work into Bear.",
    description:
      "Calculations and checks run on a full project model, developed to the level the work needs. Changes flow both ways between Bear and your tools.",
    outcome: "Each workflow builds on shared understanding.",
    model: "An ongoing project model",
    diagram: BuildDiagram,
    tab: "02 · Workflows in Bear",
    exampleTitle: "A working project model. Changes flow both ways.",
    exampleBody:
      "Bear maintains a full project model, with the level of development needed for the work at hand. Known information, sources, decisions and open questions persist across assignments. A change can start in Revit or in Bear.",
    steps: [
      [
        "Absorb",
        "If the engineer swaps the unit in Revit, Bear brings that change into its model and checks the consequences against the other project information.",
      ],
      [
        "Check & decide",
        "Run the team's calculations and checks. Choose among valid options within agreed authority; flag conflicting facts or unclear tradeoffs for the team.",
      ],
      [
        "Apply",
        "When the change is developed in Bear, carry it back into Revit after the required review. Keep the decision and its supporting sources with the project.",
      ],
    ],
    asideLabel: "How it grows",
    asideTitle: "Earn the next piece of the workflow.",
    asideBody:
      "Start with spreadsheet calculations and grow into analysis tools. Users and forward-deployed engineers can vibe-code custom workflows alongside reviewed first-party and third-party extensions. Each workflow uses and updates the same project model.",
    deliverable:
      "An updated project model + reusable workflows + coordinated changes in connected tools",
  },
  {
    id: "model",
    number: "03",
    horizon: "The destination",
    title: "Work from one living model.",
    description:
      "The model becomes the team's shared, accepted project record. Everyone works from it, with sources, decisions and unresolved questions visible in the views they need.",
    outcome: "One project record. Every perspective.",
    model: "A shared, accepted project record",
    diagram: ModelDiagram,
    tab: "03 · A shared model",
    exampleTitle: "The accepted decision becomes everyone's working view.",
    exampleBody:
      "The team develops and reviews the substitution in Bear. The accepted choice, its dependencies and supporting evidence become part of the shared project record. Unresolved questions remain visible, with detail developed wherever the work requires it.",
    steps: [
      [
        "Coordinate",
        "Bear traces effects on structure, power, ductwork, access and installation sequence. It surfaces conflicting information and decisions beyond its authority for the responsible people.",
      ],
      [
        "Reflect",
        "Procurement sees the revised bill of materials. The field sees routing and clearances. The superintendent sees conflicts and who goes first.",
      ],
      [
        "Deliver",
        "The PM sees schedule impacts; the owner sees cost and scope. Generate permit sets and other documents from the model when required.",
      ],
    ],
    asideLabel: "What makes it possible",
    asideTitle: "The model can speak everyone's language.",
    asideBody:
      "Built-in tools and custom code let users and forward-deployed engineers shape how the model behaves and represents information, from a developer's project overview to a supervisor's installation detail.",
    deliverable:
      "A shared project record + role-specific views + documents on demand",
  },
];

const collaborationPrinciples = [
  {
    title: "Cross-check what is known.",
    description:
      "Compare sources. Reconcile disagreements when the evidence allows; flag what still needs a decision.",
  },
  {
    title: "Choose within agreed bounds.",
    description:
      "Bear can choose among valid options within agreed authority. A human might pick differently and still accept the result.",
  },
  {
    title: "Learn how the team works.",
    description:
      "Keep decisions with their reasons. Distinguish a requirement, a team preference and a choice for this project.",
  },
];

function DiagramNode({
  x,
  y,
  width = 100,
  label,
  dark = false,
}: {
  x: number;
  y: number;
  width?: number;
  label: string;
  dark?: boolean;
}) {
  return (
    <g
      transform={`translate(${x} ${y})`}
      className={dark ? "ev-node ev-node-dark" : "ev-node"}
    >
      <rect width={width} height="32" rx="5" />
      <text x={width / 2} y="20" textAnchor="middle">
        {label}
      </text>
    </g>
  );
}

function ConnectDiagram() {
  const id = useId();
  return (
    <svg viewBox="0 0 360 240" role="img" aria-labelledby={id}>
      <title id={id}>
        Bear connects email and meeting notes to existing drawing and
        calculation tools, building model fragments when the task needs them and
        producing updated documents.
      </title>
      <g className="ev-wire">
        <path d="M98 47 H180 V74 M262 47 H180 M60 136 H128 M232 136 H300 M180 188 V205" />
        <circle cx="180" cy="47" r="3" />
      </g>
      <DiagramNode x={18} y={24} width={125} label="Email & assignments" />
      <DiagramNode x={217} y={24} width={125} label="Meeting notes" />
      <DiagramNode x={4} y={120} width={103} label="CAD / BIM tools" />
      <DiagramNode x={253} y={120} width={103} label="Calculations" />
      <rect
        className="ev-bear-fill"
        x="128"
        y="74"
        width="104"
        height="114"
        rx="8"
      />
      <text className="ev-bear-name" x="180" y="104" textAnchor="middle">
        Bear
      </text>
      <text className="ev-on-bear" x="180" y="121" textAnchor="middle">
        acts + coordinates
      </text>
      <g className="ev-fragments" transform="translate(149 132)">
        <rect width="27" height="25" rx="3" />
        <rect x="34" width="27" height="25" rx="3" />
        <path d="M7 8 H20 M7 16 H15 M41 8 H54 M41 16 H49" />
      </g>
      <text className="ev-on-bear" x="180" y="173" textAnchor="middle">
        Model fragments
      </text>
      <DiagramNode x={100} y={205} width={160} label="Updated documents" />
      <circle className="ev-approval-dot" cx="180" cy="196" r="4" />
    </svg>
  );
}

function BuildDiagram() {
  const id = useId();
  return (
    <svg viewBox="0 0 360 240" role="img" aria-labelledby={id}>
      <title id={id}>
        Bear runs calculations and checks on a coherent project model developed
        for the work at hand. It absorbs changes from Revit and other tools and
        applies changes back to them.
      </title>
      <DiagramNode x={181} y={8} width={157} label="Revit & other tools" />
      <g className="ev-exchange">
        <path d="M250 42 V58 M247 55 L250 58 L253 55 M283 58 V42 M280 45 L283 42 L286 45" />
        <text x="213" y="53">
          absorb
        </text>
        <text x="292" y="53">
          apply
        </text>
      </g>
      <rect
        className="ev-bear-fill"
        x="20"
        y="62"
        width="320"
        height="165"
        rx="9"
      />
      <text className="ev-bear-name" x="40" y="95">
        Bear
      </text>
      <text className="ev-on-bear" x="319" y="92" textAnchor="end">
        project knowledge + workflows
      </text>
      <DiagramNode x={38} y={113} width={133} label="Calculations" dark />
      <DiagramNode x={189} y={113} width={133} label="Design checks" dark />
      <path
        className="ev-inner-wire"
        d="M105 145 V156 H253 V145 M180 156 V168"
      />
      <g className="ev-node ev-node-dark">
        <rect x="38" y="168" width="284" height="48" rx="5" />
      </g>
      <g transform="translate(0 136) scale(0.4)">
        <ProjectModel />
      </g>
      <text className="ev-model-label" x="108" y="187">
        Project model
      </text>
      <text className="ev-on-bear" x="108" y="202">
        Detail follows the work
      </text>
    </svg>
  );
}

function ProjectModel() {
  return (
    <g className="ev-model">
      <path d="M180 83 L219 105 V159 L180 182 L141 159 V105 Z" />
      <path d="M141 105 L180 127 L219 105 M180 127 V182 M141 132 L180 154 L219 132 M160 94 L199 116 V170 M199 94 L160 116 V170" />
    </g>
  );
}

function ModelDiagram() {
  const id = useId();
  return (
    <svg viewBox="0 0 360 240" role="img" aria-labelledby={id}>
      <title id={id}>
        A shared, accepted project model in Bear serves design, procurement,
        field, superintendent, project manager and owner views, keeping sources,
        decisions and unresolved questions visible.
      </title>
      <rect
        className="ev-bear-fill"
        x="4"
        y="8"
        width="352"
        height="224"
        rx="9"
      />
      <text className="ev-bear-name" x="22" y="39">
        Bear
      </text>
      <text className="ev-on-bear" x="338" y="35" textAnchor="end">
        one model, many views
      </text>
      <g className="ev-inner-wire">
        <path d="M180 134 L71 81 M180 134 L289 81 M180 134 H71 M180 134 H289 M180 134 L71 189 M180 134 L289 189" />
      </g>
      <DiagramNode x={20} y={65} width={102} label="Design" dark />
      <DiagramNode x={238} y={65} width={102} label="Procurement" dark />
      <DiagramNode x={20} y={118} width={102} label="Field" dark />
      <DiagramNode x={238} y={118} width={102} label="Superintendent" dark />
      <DiagramNode x={20} y={173} width={102} label="PM" dark />
      <DiagramNode x={238} y={173} width={102} label="Owner / developer" dark />
      <ProjectModel />
    </svg>
  );
}

function BearEvolution() {
  return (
    <div className="bear-evolution">
      <title>Bear | From workflows to a living model</title>
      <div className="ev-toolbar ev-no-print">
        <Button variant="ghost" size="sm" className="rounded-full" asChild>
          <Link to="/prototypes">
            <ArrowLeft data-icon="inline-start" />
            Prototypes
          </Link>
        </Button>
        <span>A direction to explore</span>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => window.print()}
        >
          <Printer data-icon="inline-start" />
          Print overview
        </Button>
      </div>

      <div className="ev-sheet">
        <section className="ev-overview" aria-labelledby="evolution-title">
          <header className="ev-masthead">
            <a
              href="#evolution-title"
              className="ev-wordmark ev-brand"
              aria-label="Bear evolution"
            >
              <span aria-hidden="true">🐻</span>Bear
            </a>
            <p className="ev-eyebrow">
              Construction intelligence / A proposed evolution
            </p>
            <span className="ev-edition">Discussion draft · 02</span>
          </header>

          <div className="ev-intro">
            <h1 id="evolution-title">
              Start with the work.
              <br />
              <span>Grow into the source of truth.</span>
            </h1>
            <p>
              AI agents working alongside your team: turning changing
              requirements into coordinated designs and deliverables, while
              building a shared understanding of the project.
            </p>
          </div>

          <div className="ev-inputs">
            <span className="ev-eyebrow">The work keeps changing</span>
            <div className="ev-input-list">
              <span>Owner requirements</span>
              <span>Team decisions</span>
              <span>Field realities</span>
              <span>Supply constraints</span>
            </div>
            <ArrowRight className="size-4" aria-hidden="true" />
            <strong>Assign it to Bear</strong>
          </div>

          <div className="ev-stages">
            {stages.map(({ diagram: Diagram, ...stage }) => (
              <article className="ev-stage" key={stage.id}>
                <div className="ev-stage-label">
                  <span>{stage.number}</span>
                  <p className="ev-eyebrow">{stage.horizon}</p>
                </div>
                <h2>{stage.title}</h2>
                <div className="ev-diagram">
                  <Diagram />
                </div>
                <p className="ev-stage-description">{stage.description}</p>
                <p className="ev-outcome">
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                  {stage.outcome}
                </p>
              </article>
            ))}
          </div>

          <div className="ev-foundation">
            <div className="ev-foundation-title">
              <Box className="size-4" aria-hidden="true" />
              <span>From task context to shared project understanding</span>
            </div>
            <div className="ev-model-growth">
              {stages.map((stage) => (
                <p key={stage.id}>
                  <span className="ev-growth-dot" />
                  {stage.model}
                </p>
              ))}
            </div>
            <p className="ev-foundation-note">
              Bear's modeling knowledge is there from day one. Model detail
              follows the work at every stage. More workflows and more people
              come to rely on the same project understanding.
            </p>
          </div>

          <section
            className="ev-collaboration"
            aria-labelledby="collaboration-title"
          >
            <h2 className="ev-eyebrow" id="collaboration-title">
              A collaborator, at every stage
            </h2>
            <dl>
              {collaborationPrinciples.map((principle) => (
                <div key={principle.title}>
                  <dt>{principle.title}</dt>
                  <dd>{principle.description}</dd>
                </div>
              ))}
            </dl>
          </section>

          <footer className="ev-slide-footer">
            <p>
              <Check className="size-4" aria-hidden="true" />
              Agreed authority. Human review where the work calls for it.
            </p>
            <p>
              Solve a workflow. Connect the knowledge. Coordinate the project.
            </p>
          </footer>
        </section>

        <section
          className="ev-example ev-no-print"
          aria-labelledby="example-title"
        >
          <div className="ev-example-heading">
            <div>
              <p className="ev-eyebrow">
                Follow one change through the evolution
              </p>
              <h2 id="example-title">“The specified unit is unavailable.”</h2>
            </div>
            <span className="ev-example-hint">
              Same trigger. A growing role for Bear.
              <ArrowDown className="size-4" aria-hidden="true" />
            </span>
          </div>
          <Tabs defaultValue="connect">
            <TabsList
              aria-label="Explore the equipment substitution at each stage"
              className="ev-example-tabs rounded-full"
            >
              {stages.map((stage) => (
                <TabsTrigger
                  key={stage.id}
                  value={stage.id}
                  className="rounded-full"
                >
                  {stage.tab}
                </TabsTrigger>
              ))}
            </TabsList>
            {stages.map((stage) => (
              <TabsContent value={stage.id} key={stage.id}>
                <div className="ev-example-content">
                  <div>
                    <h3>{stage.exampleTitle}</h3>
                    <p className="ev-example-body">{stage.exampleBody}</p>
                    <ol className="ev-steps">
                      {stage.steps.map(([label, description], i) => (
                        <li key={label}>
                          <span className="ev-step-number">{i + 1}</span>
                          <div>
                            <h4>{label}</h4>
                            <p>{description}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                  <aside className="ev-example-aside">
                    <p className="ev-eyebrow">{stage.asideLabel}</p>
                    <h3>{stage.asideTitle}</h3>
                    <p>{stage.asideBody}</p>
                    <div className="ev-deliverable">
                      <span className="ev-eyebrow">What comes out</span>
                      <p>{stage.deliverable}</p>
                    </div>
                  </aside>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </section>
        <EvolutionWorkspace />
        <footer className="ev-final-footer ev-no-print">
          <div className="ev-closing">
            <span className="ev-wordmark">Bear</span>
            <p>
              Intelligence that does the work.
              <br />
              <span>Shared understanding that grows with it.</span>
            </p>
            <a href="#evolution-title">Back to the big picture ↑</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
